'use client';
import { useI18n } from '@/components/public/i18n/LocaleProvider';

import { useRef } from 'react';
import { ELINA_COPILOT as SOURCE_ELINA_COPILOT, type Capability } from '@/lib/public/universes';
import { Arrow } from './Icons';
import usePanelTransition from '../usePanelTransition';

export default function RNodePerimeters({
  capabilities,
  selectedIds,
  activeGroup,
  onGroupChange,
  onToggle,
  onContinue,
  continueDisabled,
}: {
  capabilities: Capability[];
  selectedIds: string[];
  activeGroup: string;
  onGroupChange: (group: string) => void;
  onToggle: (id: string) => void;
  onContinue: () => void;
  continueDisabled: boolean;
}) {
  const { t, href, content, locale } = useI18n();
  const ELINA_COPILOT = content(SOURCE_ELINA_COPILOT);

  const groups = [...new Set(capabilities.map((capability) => capability.group)), t('Copilote')];
  const activeIndex = Math.max(0, groups.indexOf(activeGroup));
  const currentGroup = groups[activeIndex];
  const nextGroup = groups[activeIndex + 1];
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const panelRef = usePanelTransition(currentGroup);
  const choicesFor = (group: string) =>
    group === t('Copilote')
      ? [ELINA_COPILOT]
      : capabilities.filter((capability) => capability.group === group);
  const selectedCount = choicesFor(currentGroup).filter((choice) =>
    selectedIds.includes(choice.id)
  ).length;

  const showNext = () => {
    onGroupChange(nextGroup);
    window.requestAnimationFrame(() => {
      panelRef.current?.focus({ preventScroll: true });
      panelRef.current?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
      });
    });
  };

  return (
    <div className="u-rnode-perimeters">
      <div className="u-config-heading">
        <h3>{t("Vos 4 périmètres R-Node")}</h3>
        <span>{t("Plusieurs choix possibles")}</span>
      </div>
      <p className="u-perimeter-help" id="rnode-perimeter-help">
        {t('Parcourez les 4 périmètres et choisissez ce qui vous est utile. Élina est intégrée à l’architecture de R-Node ; son activation et ses usages sont définis dans votre projet. L’option Copilote reste à sélectionner.')}</p>
      <div className="u-perimeter-toolbar">
        <div
          className="u-filter-tabs u-rnode-tabs"
          role="tablist"
          aria-label={t("Les 4 périmètres R-Node")}
        >
          {groups.map((group, index) => {
            const count = choicesFor(group).filter((choice) =>
              selectedIds.includes(choice.id)
            ).length;
            return (
              <button
                type="button"
                role="tab"
                key={group}
                id={`rnode-perimeter-tab-${index}`}
                aria-controls="rnode-perimeter-panel"
                aria-selected={currentGroup === group}
                aria-label={group + ', ' + t('Besoins sélectionnés : {count}', { count })}
                tabIndex={currentGroup === group ? 0 : -1}
                ref={(element) => {
                  tabRefs.current[index] = element;
                }}
                onPointerEnter={(event) => {
                  if (event.pointerType === 'mouse') onGroupChange(group);
                }}
                onFocus={() => onGroupChange(group)}
                onClick={() => onGroupChange(group)}
                onKeyDown={(event) => {
                  let nextIndex = index;
                  if (event.key === 'ArrowRight') nextIndex = (index + 1) % groups.length;
                  else if (event.key === 'ArrowLeft')
                    nextIndex = (index - 1 + groups.length) % groups.length;
                  else if (event.key === 'Home') nextIndex = 0;
                  else if (event.key === 'End') nextIndex = groups.length - 1;
                  else return;
                  event.preventDefault();
                  tabRefs.current[nextIndex]?.focus();
                }}
              >
                {group}
                <span
                  className={`u-perimeter-count ${count ? 'has-selection' : ''}`}
                  aria-hidden="true"
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <button
          className="u-button u-perimeter-context"
          disabled={continueDisabled}
          type="button"
          onClick={onContinue}
          aria-describedby="rnode-perimeter-help"
        >
          {t("Préciser mon contexte ")}<Arrow />
        </button>
      </div>
      <div
        id="rnode-perimeter-panel"
        className="u-perimeter-panel"
        role="tabpanel"
        aria-labelledby={`rnode-perimeter-tab-${activeIndex}`}
        ref={panelRef}
        tabIndex={0}
      >
        <div className="u-perimeter-progress">
          <span>
            {t("Périmètre ")}{activeIndex + 1} {t('sur')} {groups.length} ·{' '}
            <strong>{currentGroup === t('Copilote') ? t('Elina Copilote') : currentGroup}</strong>
          </span>
          <span>
            {selectedCount
              ? t('Besoins sélectionnés : {count}', { count: selectedCount })
              : t('À choisir selon vos besoins')}
          </span>
        </div>
        {currentGroup === t('Copilote') ? (
          <div className="u-copilot-option">
            <p className="u-eyebrow">{t("Recommandé avec R-Node · En option")}</p>
            <label>
              <input
                type="checkbox"
                checked={selectedIds.includes(ELINA_COPILOT.id)}
                onChange={() => onToggle(ELINA_COPILOT.id)}
              />
              <span>
                <strong>{t("Elina Copilote")}</strong>
                <small>{ELINA_COPILOT.detail}</small>
              </span>
            </label>
            <p className="u-copilot-control">
              {t("Vous choisissez ses accès. Votre équipe garde la validation des actions.")}</p>
          </div>
        ) : (
          <div className="u-capabilities">
            {choicesFor(currentGroup).map((capability) => (
              <label
                key={capability.id}
                className={`u-capability ${selectedIds.includes(capability.id) ? 'is-selected' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(capability.id)}
                  onChange={() => onToggle(capability.id)}
                />
                <span>
                  <strong>{capability.title}</strong>
                  <small>{capability.detail}</small>
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
      <div className="u-perimeter-next">
        <p>
          {nextGroup
            ? t('Vous pouvez passer ce périmètre et y revenir.')
            : t('Votre sélection est prête ? Passez à votre contexte.')}
        </p>
        <button
          type="button"
          className="u-button u-button-secondary"
          onClick={nextGroup ? showNext : onContinue}
          disabled={!nextGroup && continueDisabled}
        >
          {nextGroup
            ? `Suivant : ${nextGroup === t('Copilote') ? t('Elina Copilote') : nextGroup}`
            : t('Préciser mon contexte')}{' '}
          <Arrow />
        </button>
      </div>
    </div>
  );
}


